import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Lock, Sparkles, Crown } from "lucide-react";
import { usePlanLimits } from "@/hooks/use-plan-limits";
import type { PlanName } from "@/hooks/use-subscription";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface PlanGateProps {
  tier: Exclude<PlanName, "free">;
  featureName: string;
  description?: string;
  children: ReactNode;
}

const TIER_LABEL: Record<Exclude<PlanName, "free">, string> = {
  pro: "Pro",
  premium: "Premium",
};

export function PlanGate({ tier, featureName, description, children }: PlanGateProps) {
  const { has, loading } = usePlanLimits();

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        Carregando…
      </div>
    );
  }

  if (has(tier)) return <>{children}</>;

  const Icon = tier === "premium" ? Crown : Sparkles;

  return (
    <Card className="border-lt-border bg-lt-bg2">
      <CardContent className="flex flex-col items-center justify-center gap-5 px-6 py-16 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
          <Lock className="h-6 w-6 text-primary" />
        </div>
        <div className="space-y-2">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-primary">
            <Icon className="h-3 w-3" />
            Plano {TIER_LABEL[tier]}
          </div>
          <h2 className="font-serif text-2xl font-light text-foreground">
            {featureName} é um recurso {TIER_LABEL[tier]}
          </h2>
          {description && (
            <p className="mx-auto max-w-md text-sm leading-relaxed text-muted-foreground">
              {description}
            </p>
          )}
        </div>
        <div className="flex flex-col items-center gap-3 sm:flex-row">
          <Button asChild size="lg" className="font-medium">
            <Link to="/planos">Conhecer o {TIER_LABEL[tier]}</Link>
          </Button>
          <Button asChild variant="ghost" size="lg">
            <Link to="/app">Voltar ao painel</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}